import type { SubscriptionResponse } from '@kimuno/api-sdk';
import CloseIcon from '@mui/icons-material/Close';
import { Box, type ButtonProps, Dialog, DialogTitle, IconButton, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { palette, spaces } from '../../assets/styles/themes/tokens';
import { colors } from '../../assets/styles/themes/variables';
import CancelIcon from '../../assets/subscription/cancel.svg';
import UpgradeIcon from '../../assets/subscription/manageSubscription-upgrade.svg';
import PauseIcon from '../../assets/subscription/pause.svg';
import WithdrawalIcon from '../../assets/subscription/withdrawal-plan.svg';
import { manageSubscriptionSteps, SubscriptionCancellationType } from '../../constants';
import {
	ampliTrackSubscriptionCancellationSelectedEvent,
	ampliTrackSubscriptionPauseSelectedEvent,
} from '../../services/track.service';
import type { User } from '../../types/User';
import { isWithinWithdrawalWindow } from '../../utils/withdrawalEligibility';
import {
	CancelSubscriptionDialogContent,
	ManageSubscriptionDialogContent,
	PauseSubscriptionDialogContent,
	WithdrawSubscriptionDialogContent,
} from './DialogContent';
export interface OwnProps {
	user: User;
	subscription: SubscriptionResponse;
	open: boolean;
	onClose: () => void;
	onClickUpgrade: () => void;
}

export type DialogStep = (typeof manageSubscriptionSteps)[keyof typeof manageSubscriptionSteps];

type DialogContentProps = {
	[K in DialogStep]: React.ReactNode;
};

export interface PlanSetting {
	id: string;
	title: string;
	description: string;
	buttonText: string;
	buttonVariant?: ButtonProps['variant'];
	buttonColor?: ButtonProps['color'];
	onClick: () => void;
	Icon: React.ElementType;
	showDivider?: boolean;
	buttonSx?: any;
}

const ManageSubscriptionDialog: React.FC<OwnProps> = (props) => {
	const { t } = useTranslation();
	const { user, subscription, open, onClose, onClickUpgrade } = props;
	const [step, setStep] = useState<DialogStep>(manageSubscriptionSteps.manage);
	const canUpgrade = !!user?.canUpgrade;
	const isInTrial = !!subscription?.trial?.isInTrial;
	const withinWithdrawalWindow = isWithinWithdrawalWindow(subscription);

	const planSettings: Record<string, PlanSetting> = {
		upgradeSetting: {
			id: 'upgrade',
			title: t('Adjust my subscription'),
			description: t('Flexibly customize your plan so you get exactly what you need.'),
			buttonText: t('Adjust plan'),
			buttonVariant: 'outlined',
			buttonColor: 'inherit',
			onClick: onClickUpgrade,
			Icon: UpgradeIcon,
			showDivider: true,
		},
		pauseSetting: {
			id: 'pause',
			title: t('Pause my subscription'),
			description: t(
				"Take a break of up to 3 months if you don't need support with lesson preparation."
			),
			buttonText: t('Pause now'),
			buttonVariant: 'outlined',
			buttonColor: 'inherit',
			onClick: () => {
				ampliTrackSubscriptionPauseSelectedEvent('');
				setStep(manageSubscriptionSteps.pause);
			},
			Icon: PauseIcon,
			showDivider: true,
			buttonSx: {
				':hover': {
					backgroundColor: colors.neutral200,
				},
			},
		},
		withdrawalSetting: {
			id: 'withdrawal',
			title: t('Withdraw'),
			description: t(
				'For online-concluded contracts, you can withdraw within the withdrawal period — ' +
					'independent of cancellation at the end of the contract.'
			),
			buttonText: t('Confirm withdrawal'),
			buttonVariant: 'outlined',
			buttonColor: 'inherit',
			onClick: () => {
				ampliTrackSubscriptionCancellationSelectedEvent(
					false,
					'',
					SubscriptionCancellationType.RevocationRequested
				);
				setStep(manageSubscriptionSteps.withdraw);
			},
			Icon: WithdrawalIcon,
			showDivider: true,
			buttonSx: {
				':hover': {
					backgroundColor: colors.neutral200,
				},
			},
		},
		cancelSetting: {
			id: 'cancel',
			title: isInTrial ? t('Cancel my Trial Subscription') : t('Cancel my subscription'),
			description: t(
				"Want to cancel your subscription? It's a shame, but it's certainly possible. " +
					'Cancel your subscription at the end of its term with just a few clicks.'
			),
			buttonText: isInTrial ? t('Cancel Trial Subscription') : t('Cancel subscription'),
			buttonColor: 'error',
			onClick: () => {
				ampliTrackSubscriptionCancellationSelectedEvent(
					isInTrial,
					'',
					SubscriptionCancellationType.CancellationRequested
				);
				setStep(manageSubscriptionSteps.cancel);
			},
			Icon: CancelIcon,
			showDivider: false,
		},
	};

	const getColumns = (): PlanSetting[] => {
		const columns: PlanSetting[] = [];

		if (canUpgrade) columns.push(planSettings.upgradeSetting);
		if (!isInTrial) columns.push(planSettings.pauseSetting);
		// Trial exclusion (in-trial / converted / school trial) lives inside isWithinWithdrawalWindow.
		if (withinWithdrawalWindow) columns.push(planSettings.withdrawalSetting);

		columns.push(planSettings.cancelSetting);

		return columns;
	};

	useEffect(() => {
		if (open) setStep('manageSubscription');
	}, [open]);

	const planSettingsColumns = getColumns();
	const getDialogWidth = () => {
		if (step !== manageSubscriptionSteps.manage) return 'lg';
		if (planSettingsColumns.length === 1) return 'sm';
		if (planSettingsColumns.length > 3) return 'xl';
		return 'lg';
	};
	const dialogWidth = getDialogWidth();

	const dialogContent: DialogContentProps = {
		manageSubscription: <ManageSubscriptionDialogContent columns={planSettingsColumns} />,
		pauseSubscription: (
			<PauseSubscriptionDialogContent
				setStep={setStep}
				onClose={onClose}
				user={user}
				subscription={subscription}
			/>
		),
		cancelSubscription: (
			<CancelSubscriptionDialogContent
				setStep={setStep}
				onClose={onClose}
				endDate={subscription.endDate}
			/>
		),
		withdrawSubscription: (
			<WithdrawSubscriptionDialogContent
				setStep={setStep}
				onClose={onClose}
				user={user}
				subscription={subscription}
			/>
		),
	};

	const dialogTitle: { [key: string]: string } = {
		manageSubscription: t('Manage my subscription'),
		pauseSubscription: t('Pause my subscription'),
		cancelSubscription: t('Cancel my subscription'),
		withdrawSubscription: t('Withdraw contract'),
	};

	return (
		<Dialog onClose={onClose} open={open} fullWidth={true} maxWidth={dialogWidth}>
			<DialogTitle
				component="div"
				sx={{
					paddingTop: spaces.lg,
					paddingBottom: spaces.lg,
					borderBottom: `1px solid ${palette.elevation.outlined}`,
					display: 'flex',
					flexDirection: 'column',
					gap: spaces.xs,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Typography variant="h5" color={palette.text.primary}>
						{dialogTitle[step] || t('Manage my subscription')}
					</Typography>
					<Box>
						<IconButton color="default" size="small" onClick={onClose} data-testid="close-button">
							<CloseIcon />
						</IconButton>
					</Box>
				</Box>
			</DialogTitle>
			{dialogContent[step]}
		</Dialog>
	);
};

export default ManageSubscriptionDialog;
